import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '@tackle-io/platform-ui';
import { Box } from 'vendor/material';
import { authStore } from 'stores/Auth';
import { useDocumentTitle, useIsSandboxEnvironment } from 'hooks';
import { useCoSellContext } from '../../CoSellContextProvider';
import { ErrorBoundary, Page } from 'components';
import useStyles from './CoSellLandingPage.styles';
import { ErrorPage } from 'components/ErrorPage/ErrorPage';
import { CoSellHeader, CoSellCloudMenu, CoSellTabs } from './components';
import { DisplayCloudType } from '../../types/enums';
import CoSellDateFilterWarning from './components/CoSellDateFilterWarning/CoSellDateFilterWarning';
import { START_DATE_URL_KEY, TAB_KEY } from './helpers/urlParamsHelper';
import PersistDismissableBanner from 'components/PersistDismissableBanner/PersistDismissableBanner';
import { CO_SELL_DASHBOARD_PATH_LEGACY } from 'pages/CoSell/utilities/constants/CoSellConstants';
import { COSELL_AWS_MANAGE_OPPORTUNITIES_V3_URL } from 'utils/constants';
import useIsV2Customer from 'hooks/useIsV2Customer';
import useIsV3MigrationEnabled from 'hooks/useIsV3MigrationEnabled';

const CoSellLandingPage = () => {
  const classes = useStyles();
  const { vendorId } = useCoSellContext();
  const [showBanner, setShowBanner] = useState(true);
  const userId = authStore.profile?.sub;

  useDocumentTitle('Co-Sell opportunities');
  const location = useLocation();

  const hasOpportunityDateFilter = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const startDate = searchParams.get(START_DATE_URL_KEY);
    const tab = searchParams.get(TAB_KEY);
    return !!startDate && tab !== 'invitations';
  }, [location]);

  const isV3MigrationEnabled = useIsV3MigrationEnabled();
  const isSandboxEnvironment = useIsSandboxEnvironment();
  const isV2Customer = useIsV2Customer();
  const shouldShowBanner =
    isSandboxEnvironment ||
    (isV3MigrationEnabled && isV2Customer && showBanner);

  const bannerContent = (
    <>
      You are now in the new Tackle Co-sell experience. View the{' '}
      <Link to={CO_SELL_DASHBOARD_PATH_LEGACY}>legacy experience</Link> or{' '}
      <Link external to={COSELL_AWS_MANAGE_OPPORTUNITIES_V3_URL}>
        learn more.
      </Link>
    </>
  );

  return (
    <CoSellLandingPageContainer>
      <Page className={classes.container}>
        {shouldShowBanner && (
          <Box mt={3}>
            <PersistDismissableBanner
              id="co-sell-legacy-experience"
              content={bannerContent}
              onClose={() => setShowBanner(false)}
              userId={userId}
              vendorId={vendorId}
            />
          </Box>
        )}
        <CoSellHeader />
        <CoSellCloudMenu />
        {hasOpportunityDateFilter && <CoSellDateFilterWarning />}
        <CoSellTabs />
      </Page>
    </CoSellLandingPageContainer>
  );
};

interface CoSellLandingPageContainerProps {
  vendorIdFromCanvas?: string;
  tokenFromCanvas?: string;
  opportunityId?: string;
  cloud?: DisplayCloudType;
  children?: React.ReactElement;
}

export const CoSellLandingPageContainer = ({
  children,
}: CoSellLandingPageContainerProps) => {
  return (
    <ErrorBoundary
      renderError={() => <ErrorPage title="Co-sell opportunities" />}
    >
      {children}
    </ErrorBoundary>
  );
};

export default CoSellLandingPage;
